import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  IconButton,
  MenuItem,
  Select,
} from "@mui/material";
import { ArrowBackIosNew, ArrowForwardIos } from "@mui/icons-material";
import {
  fetchGeneratedSOAs,
} from "../services/SOAService";
import { COLORS } from "../assets/theme";
import CustomCheckbox from "./CustomCheckbox";
import { useProfileByIdQuery } from "../services/ProfileService";

const DEFAULT_PAGE_SIZE = 5;
const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

interface SOAItem {
  id: string;
  ref: string;
  profileId: string;
}

interface SendSOAEmailDialogProps {
  onSelectedIdsChange: (selectedIds: string[]) => void;
  onAttachCcEmailChange?: (attachCcEmail: boolean) => void;
}

interface SOATableRowProps {
  item: SOAItem;
  isSelected: boolean;
  onCheckboxChange: (id: string, checked: boolean) => void;
}

const mapSOAs = (content: any[]): SOAItem[] =>
  content.map((soa: any) => ({
    id: soa.id,
    ref: soa.ref || soa.id,
    profileId: soa.profileId || "",
  }));

const SOATableRow: React.FC<SOATableRowProps> = ({
  item,
  isSelected,
  onCheckboxChange,
}) => {
  const { data: profile, isLoading: profileLoading } = useProfileByIdQuery(
    item.profileId
  );
  const profileName = profile ? profile.name : "Unknown Name";

  return (
    <TableRow hover>
      <TableCell padding="checkbox" sx={{ border: "none" }}>
        <CustomCheckbox
          checked={isSelected}
          onChange={(e) => onCheckboxChange(item.id, e.target.checked)}
          ariaLabel={`Select SOA ${item.ref}`}
        />
      </TableCell>
      <TableCell sx={{ border: "none" }}>{item.ref}</TableCell>
      <TableCell sx={{ border: "none" }}>
        {profileLoading ? "Loading..." : profileName}
      </TableCell>
    </TableRow>
  );
};

const SendSOAEmailDialog: React.FC<SendSOAEmailDialogProps> = ({
  onSelectedIdsChange,
  onAttachCcEmailChange,
}) => {
  const [soas, setSoas] = useState<SOAItem[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [attachCcEmail, setAttachCcEmail] = useState<boolean>(true);
  const [page, setPage] = useState<number>(0);
  const [size, setSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalElements, setTotalElements] = useState<number>(0);
  // Ids of every SOA across all pages, fetched lazily the first time
  // "select all" is used so the selection is not limited to the open page.
  const allIdsRef = useRef<string[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchSOAList = async () => {
      setLoading(true);
      try {
        const result = await fetchGeneratedSOAs({ page, size });
        if (cancelled) return;
        setSoas(mapSOAs(result.content ?? []));
        setTotalPages(result.totalPages ?? 0);
        setTotalElements(result.totalElements ?? 0);
      } catch (error: any) {
        if (cancelled) return;
        console.error("Error fetching SOAs:", error);
        setSoas([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchSOAList();
    return () => {
      cancelled = true;
    };
  }, [page, size]);

  const handleCheckboxChange = useCallback((id: string, checked: boolean) => {
    setSelectedIds((prev) =>
      checked ? [...prev, id] : prev.filter((sid) => sid !== id)
    );
  }, []);

  useEffect(() => {
    onSelectedIdsChange(selectedIds);
  }, [selectedIds, onSelectedIdsChange]);

  useEffect(() => {
    onAttachCcEmailChange?.(attachCcEmail);
  }, [attachCcEmail, onAttachCcEmailChange]);

  const handleSelectAllChange = useCallback(
    async (checked: boolean) => {
      if (!checked) {
        setSelectedIds([]);
        return;
      }
      if (allIdsRef.current) {
        setSelectedIds(allIdsRef.current);
        return;
      }
      if (totalElements <= soas.length) {
        const ids = soas.map((soa) => soa.id);
        allIdsRef.current = ids;
        setSelectedIds(ids);
        return;
      }
      setLoading(true);
      try {
        const result = await fetchGeneratedSOAs({
          page: 0,
          size: totalElements,
        });
        const ids = mapSOAs(result.content ?? []).map((soa) => soa.id);
        allIdsRef.current = ids;
        setSelectedIds(ids);
      } catch (error: any) {
        console.error("Error selecting all SOAs:", error);
      } finally {
        setLoading(false);
      }
    },
    [soas, totalElements]
  );

  const allSelected = totalElements > 0 && selectedIds.length === totalElements;
  const someSelected = selectedIds.length > 0 && !allSelected;
  const headerCellSx = {
    border: "none",
    borderBottom: `1px solid ${COLORS.GOLD}`,
  };

  return (
    <Box>
      <Card sx={{ width: "100%", margin: "auto", boxShadow: "none", border: "none" }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 2 }}>
            <CustomCheckbox
              checked={attachCcEmail}
              onChange={(e) => setAttachCcEmail(e.target.checked)}
              label="CC: Profiles' Account Manager(s)"
            />
          </Box>
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={40} sx={{ color: COLORS.GOLD }} />
            </Box>
          ) : soas.length > 0 ? (
            <>
              <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={headerCellSx}>
                        <CustomCheckbox
                          checked={allSelected}
                          indeterminate={someSelected}
                          onChange={(e) =>
                            handleSelectAllChange(e.target.checked)
                          }
                          ariaLabel="Select all SOAs"
                        />
                      </TableCell>
                      <TableCell sx={headerCellSx}>SOA Ref</TableCell>
                      <TableCell sx={headerCellSx}>Profile Name</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {soas.map((soa) => {
                      const isSelected = selectedIds.includes(soa.id);
                      return (
                        <SOATableRow
                          key={soa.id}
                          item={soa}
                          isSelected={isSelected}
                          onCheckboxChange={handleCheckboxChange}
                        />
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box
                display="flex"
                alignItems="center"
                flexWrap="wrap"
                gap={{ xs: 1, sm: 2 }}
                sx={{ mt: 1, px: 1 }}
              >
                <Box display="flex" alignItems="center" gap={{ xs: 0.5, sm: 1 }}>
                  <IconButton
                    disabled={page <= 0}
                    onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
                    aria-label="Previous page"
                    sx={{
                      color: page > 0 ? COLORS.placeHolderColor : "#ccc",
                      p: { xs: 0.5, sm: 1 },
                    }}
                  >
                    <ArrowBackIosNew fontSize="small" />
                  </IconButton>
                  <Typography
                    sx={{
                      fontSize: { xs: "13px", sm: "14px" },
                      fontWeight: "bold",
                      color: COLORS.placeHolderColor,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Page {page + 1} of {Math.max(totalPages, 1)}
                  </Typography>
                  <IconButton
                    disabled={page + 1 >= totalPages}
                    onClick={() =>
                      setPage((prev) => Math.min(prev + 1, totalPages - 1))
                    }
                    aria-label="Next page"
                    sx={{
                      color:
                        page + 1 < totalPages
                          ? COLORS.placeHolderColor
                          : "#ccc",
                      p: { xs: 0.5, sm: 1 },
                    }}
                  >
                    <ArrowForwardIos fontSize="small" />
                  </IconButton>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  <Typography
                    sx={{
                      fontSize: { xs: "11px", sm: "0.875rem" },
                      color: COLORS.placeHolderColor,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Rows per page:
                  </Typography>
                  <Select
                    value={size}
                    onChange={(e) => {
                      setSize(Number(e.target.value));
                      setPage(0);
                    }}
                    inputProps={{ "aria-label": "Rows per page" }}
                    sx={{
                      height: { xs: "24px", sm: "32px" },
                      fontSize: { xs: "11px", sm: "0.875rem" },
                      width: { xs: "58px", sm: "80px" },
                      minWidth: { xs: "58px", sm: "80px" },
                      "& .MuiSelect-select": {
                        padding: { xs: "2px 4px", sm: "4px 8px" },
                      },
                    }}
                  >
                    {PAGE_SIZE_OPTIONS.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>

                <Typography
                  sx={{
                    fontSize: { xs: "11px", sm: "0.875rem" },
                    color: COLORS.placeHolderColor,
                    ml: "auto",
                    whiteSpace: "nowrap",
                  }}
                >
                  {selectedIds.length} selected of {totalElements}
                </Typography>
              </Box>
            </>
          ) : (
            <Typography variant="body1" color="textSecondary" align="center" sx={{ py: 4 }}>
              No SOAs available.
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default SendSOAEmailDialog;
